// src/utils/dealRouteState.js
// Phase 55 Plan 39: /transactions/<id> renders the deal form, a loading state,
// or a "deal not found" state. A missing, migrated, or hidden id must never
// mount an empty editable form (the save refuses, so the owner cannot do
// anything).
//
// `resolvedTransaction` is the deal as found in the STORE (the own `all` slice,
// or a team row). `routeStateTransaction` is a stand-in handed over outside the
// store: location.state (initialTransaction for a freshly created deal,
// parentViewChildTransaction for a TAW parent-view child) or the row the user
// clicked. A stand-in covers the window before the store has loaded, so judging
// by the store alone would not flash not-found.
//
// Once the store HAS loaded, a stand-in is trusted only when it cannot be in
// the own slice by design: a team (parent-view) child, which carries
// `_workspaceId`, or a reopened failed save (`routeStateTrusted`), whose deal
// Firestore rolled back. Anything else is stale history state, for example a
// deal deleted and then reached again with the browser Back button: it must
// not reopen as an editable form. A freshly created deal needs no trust here,
// because Firestore's latency compensation puts the local write into the
// slice's snapshot at once.
//
// A parent workspace owner's team (TAW) deals are a second source. Under the
// default "own" team scope the store holds no team rows at all, so a member's
// deal reached by link is found by a one-shot team read (useTawDealLookup,
// asked for only when needsTeamDealLookup says so). For a parent owner the
// id is judged missing only after BOTH the own `all` slice (`allLoaded`) and
// that team read (`teamLoaded`) have loaded; until then the route shows the
// 'loading' state, never a false "deal not found". A non-team workspace never
// waits on a team read.
import { isMigratedDeal } from './migratedDeals';

// The deal for a /transactions/<id> URL as found in the STORE: the own `all`
// slice first, then the rows the list is showing, then (for a parent workspace
// owner) every team (TAW) deal delivered so far, REGARDLESS of the list's
// team-scope filter, and last the deal the one-shot team read found
// (`teamLookupDeal`). A deep link to a team member's deal is not judged by
// what the list happens to show.
export function findStoreDeal({
    urlTransactionId,
    allTransactions = [],
    displayTransactions = [],
    childTransactions = [],
    isParentWorkspaceOwner = false,
    teamLookupDeal = null,
    enrich = (t) => t,
} = {}) {
    if (!urlTransactionId) return null;
    const own = (allTransactions || []).find((t) => t.id === urlTransactionId)
        ?? (displayTransactions || []).find((t) => t.id === urlTransactionId);
    if (own) return own;
    if (!isParentWorkspaceOwner) return null;
    const teamRow = (childTransactions || []).find((t) => t.id === urlTransactionId)
        ?? (teamLookupDeal?.id === urlTransactionId ? teamLookupDeal : null);
    return teamRow ? enrich(teamRow) : null;
}

// Whether the route must ask the team agent workspaces for this id: only for
// a parent owner, only once the own slice has loaded without it, and never
// when route state already explains its absence (a team child carries
// `_workspaceId`; a reopened failed save is trusted). Stale history state of
// an own deal still asks, so a deleted deal can be judged missing.
export function needsTeamDealLookup({
    urlTransactionId,
    isParentWorkspaceOwner = false,
    allLoaded = false,
    storeDeal = null,
    routeStateTransaction = null,
    routeStateTrusted = false,
} = {}) {
    if (!urlTransactionId || !isParentWorkspaceOwner || !allLoaded || storeDeal) return false;
    if (routeStateTransaction?._workspaceId || routeStateTrusted) return false;
    return true;
}

export function resolveDealRouteState({
    urlTransactionId,
    resolvedTransaction = null,
    routeStateTransaction = null,
    allLoaded = false,
    isParentWorkspaceOwner = false,
    teamLoaded = false,
    routeStateTrusted = false,
} = {}) {
    if (!urlTransactionId) return 'form';
    if (resolvedTransaction) return isMigratedDeal(resolvedTransaction) ? 'not-found' : 'form';
    if (routeStateTransaction && !isMigratedDeal(routeStateTransaction)) {
        if (!allLoaded || routeStateTrusted || routeStateTransaction._workspaceId) return 'form';
    }
    if (!allLoaded) return 'form';
    return isParentWorkspaceOwner && !teamLoaded ? 'loading' : 'not-found';
}
